import { useCallback, useMemo } from 'react';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { getDataGridLocalization, getLLDate, MgPrisLocale } from '@retail/utils';
import { dataGridBoldClass } from '@shared/styles';
import dayjs from 'dayjs';
import { Box } from '@mui/material';
import { ConditionsNew } from '@retail/calculus/types';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsTableActions, LevelChip } from '@retail/calculus/components';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { HierarchyLevels } from '@retail/products/types';
import { useRowColorGrouping } from '@retail/hooks';

interface Props {
  conditions: ConditionsNew[];
  loading?: boolean;
  multipleChains?: boolean;
  supplierLevel?: boolean;
  openCreateDialog?: () => void;
  handleDeactivate?: (condition: number) => void;
  handleEdit?: (condition: ConditionsNew) => void;
}

const baseColumnProps: Partial<GridColDef<ConditionsNew>> = {
  width: 140,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

export function CustomerMarkupTable({
  conditions,
  loading,
  multipleChains,
  supplierLevel,
  openCreateDialog,
  handleDeactivate,
  handleEdit,
}: Props) {
  const t = useCalculusTFunction();
  const { i18n } = useTranslation();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const showActions = useCallback(
    (bindingType?: HierarchyLevels) =>
      (supplierLevel && bindingType !== 'ASSORTMENT') || !supplierLevel,
    [supplierLevel]
  );

  const columns: GridColDef<ConditionsNew>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'customer',
        headerName: t('calculus.level.customer'),
        width: 190,
        renderCell: ({ row }) => row.assortmentName,
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'level',
        headerName: t('calculus.level.level'),
        width: 190,
        renderCell: ({ row }) => (
          <LevelChip
            active={!row.inDraft}
            bindingType={row.bindingType}
            bindingTargetItem={row.bindingTargetItem}
          />
        ),
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'supplier',
        headerName: t('calculus.level.supplier'),
        width: 190,
        valueGetter: (_, row) => row.supplier?.supplierNumber,
        renderCell: ({ row }) =>
          row.supplier ? (
            <StackedTextCell topLine={row.supplier.name} bottomLine={row.supplier.supplierNumber} />
          ) : (
            '–'
          ),
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        field: 'name',
        headerName: t('calculus.level.levelOrItemName'),
        width: 260,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
        ),
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        headerAlign: 'center',
        align: 'center',
        field: 'salesFactor',
        headerName: t('calculus.customerMarkup.factor'),
        width: 100,
        renderCell: ({ row }) =>
          row.customerMarkupCondition?.salesFactor
            ? `${formatPrice(row.customerMarkupCondition?.salesFactor, i18n.language)}`
            : '-',
      },
      {
        ...baseColumnProps,
        cellClassName: () => dataGridBoldClass,
        headerAlign: 'center',
        align: 'center',
        field: 'percent',
        headerName: t('calculus.customerMarkup.percent'),
        width: 100,
        renderCell: ({ row }) =>
          row.customerMarkupCondition?.percent
            ? `${formatPrice(row.customerMarkupCondition?.percent, i18n.language)} %`
            : '-',
      },
      {
        ...baseColumnProps,
        field: 'activeFromTo',
        width: 160,
        headerName: t('calculus.activeFromTo'),
        renderCell: ({ row }) => {
          const activeFrom = getLLDate(row.customerMarkupCondition?.validFrom);
          const activeTo =
            row.customerMarkupCondition?.validTo &&
            dayjs(row.customerMarkupCondition?.validTo).format('ll');
          return <StackedTextCell topLine={activeFrom} bottomLine={activeTo} centerAlign />;
        },
      },
      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        width: 90,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <ConditionsTableActions
            handleDeactivate={
              !row.inDraft && showActions(row.bindingType) && handleDeactivate
                ? () => handleDeactivate(row.customerMarkupCondition!.conditionId)
                : undefined
            }
            handleEdit={
              handleEdit && showActions(row.bindingType) ? () => handleEdit(row) : undefined
            }
          />
        ),
      },
    ],
    [handleDeactivate, handleEdit, i18n.language, showActions, t]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium
          getRowId={(row) => row.customerMarkupCondition!.conditionId}
          getRowClassName={getRowColorForStripedTable}
          columns={columns}
          rows={conditions}
          rowHeight={70}
          loading={loading}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          localeText={getDataGridLocalization(i18n.language as MgPrisLocale)}
          columnVisibilityModel={{ customer: !!multipleChains, supplier: !supplierLevel }}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('calculus.noCustomerMarkup')}
                addAction={
                  <AddButton onClick={openCreateDialog}>
                    {t('calculus.createCustomerMarkup')}
                  </AddButton>
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
}
